import { US_COUNTRY_VALUE } from "constants/addresses";
import {
  INSURANCE_PAYMENT_SOURCE,
  MANAGED_CARE_PAYMENT_SOURCE,
  PAYMENT_SOURCE_TYPE
} from "constants/patients";
import { TASK_STATUS_COMPLETED, TASK_STATUS_TODO } from "constants/staff-tasks";
import { DEFAULT_PATIENTS_LIMIT } from "services/patients";
import { type PatientNote } from "services/types/patient-notes.type";
import type { Patient, PatientFilters } from "services/types/patients.type";
import { type StaffTask } from "services/types/staff-tasks.type";

import userPhotoFallback from "../assets/images/user.png";
import { formatClinic } from "./clinics.util";
import {
  formatStaffMemberFullName,
  formatValidQueryParamUrlCommaSeparated,
  getFullName
} from "./common";

export const formatPatient = (patient: Patient) => {
  const fullName = getFullName({
    firstName: patient.first_name,
    middleName: patient.middle_name || "",
    preferredName: patient.preferred_name || "",
    lastName: patient.last_name,
    title: patient.title,
    suffix: patient.suffix
  });

  const result = {
    ...patient,
    staff_dialog_assignee:
      patient.staff_dialog_assignee &&
      formatStaffMemberFullName(patient.staff_dialog_assignee),
    paymentSourceType: patient.payment_source_type,
    fullName,
    preferred_clinic:
      patient.preferred_clinic && formatClinic(patient.preferred_clinic),
    preferred_provider:
      patient.preferred_provider &&
      formatStaffMemberFullName(patient.preferred_provider),
    avatar: patient.photo ?? userPhotoFallback,
    totalBalance: parseFloat(patient.balance || "0"),
    patient_staff_tasks: patient.patient_staff_tasks?.map((task) => ({
      ...formatPatientStaffTask(task),
      patient: {
        id: patient.id
      }
    })),
    patient_notes: patient.patient_notes?.map((note) => ({
      ...formatPatientNote(note),
      patient: {
        id: patient.id
      }
    }))
  };

  return result;
};

export const getFullAddress = (
  patient: Patient,
  showStreetAddressTwo = false
) => {
  const address = [
    patient.street_address_1,
    `${
      showStreetAddressTwo && patient.street_address_2
        ? `${patient.street_address_2} `
        : ""
    }${patient.city || ""}`,
    `${patient.state || ""} ${patient.zip_code || ""}`.trim(),
    patient.country?.replace(US_COUNTRY_VALUE, "USA")
  ].filter(Boolean);

  return address.join(", ");
};

export function getPatientPaymentSourceDisplay(patient?: Patient | null) {
  if (patient === null || patient === undefined) {
    return "";
  }
  if (
    patient.payment_source_type === INSURANCE_PAYMENT_SOURCE &&
    patient.primary_insurance_policy
  ) {
    return patient.primary_insurance_policy.insurer?.name;
  } else if (
    patient.payment_source_type === MANAGED_CARE_PAYMENT_SOURCE &&
    patient.managed_care_plan
  ) {
    return patient.managed_care_plan.name;
  } else {
    return PAYMENT_SOURCE_TYPE.getFromCode(patient?.payment_source_type);
  }
}

export const formatPatientStaffTask = (task: StaffTask) => {
  const { created_by = {}, assignee = {}, patient = {} } = task;

  const fullName = patient
    ? getFullName({
        firstName: patient.first_name,
        middleName: patient.middle_name || "",
        preferredName: patient.preferred_name || "",
        lastName: patient.last_name,
        title: patient.title,
        suffix: patient.suffix
      })
    : "";

  let status = task.status;
  //if task created earlier it has status value = undefined
  if (task.completed_at && !task.status) {
    status = TASK_STATUS_COMPLETED;
  } else if (!task.completed_at && !task.status) {
    status = TASK_STATUS_TODO;
  }
  const result: StaffTask = {
    ...task,
    status,
    created_by: {
      ...created_by,
      fullName: getFullName({
        firstName: created_by.first_name || "",
        lastName: created_by.last_name || "",
        title: created_by.title,
        suffix: created_by.suffix
      })
    },
    assignees: task.assignees?.map((assignee) => ({
      ...assignee,
      user: assignee.user
        ? {
            ...assignee.user,
            fullName: getFullName({
              firstName: assignee.user.first_name || "",
              lastName: assignee.user.last_name || "",
              title: assignee.user.title,
              suffix: assignee.user.suffix
            })
          }
        : assignee.user
    })),
    assignee: assignee
      ? {
          ...assignee,
          fullName: getFullName({
            firstName: assignee.first_name || "",
            lastName: assignee.last_name || "",
            title: assignee.title,
            suffix: assignee.suffix
          })
        }
      : assignee,
    patient: task.patient
      ? {
          ...task.patient,
          fullName
        }
      : task.patient
  };

  return result;
};

export const formatPatientNote = (note: PatientNote) => {
  const { created_by = {}, patient = {} } = note;

  const fullName = getFullName({
    firstName: patient.first_name,
    middleName: patient.middle_name || "",
    preferredName: patient.preferred_name || "",
    lastName: patient.last_name,
    title: patient.title,
    suffix: patient.suffix
  });

  const result: PatientNote = {
    ...note,
    created_by: {
      ...created_by,
      fullName: getFullName({
        firstName: created_by.first_name || "",
        lastName: created_by.last_name || "",
        title: created_by.title,
        suffix: created_by.suffix
      })
    },
    patient: {
      ...note.patient,
      fullName
    }
  };

  return result;
};

export const formatPatientFilters = (filters: PatientFilters) => {
  const { customFilters, baseFilter, ...apiFilters } = filters;

  return {
    // values coming only from the apiFilters
    ids: apiFilters?.ids,
    search: apiFilters?.search,

    // values coming from both apiFilters, baseFilter in the order of priority
    is_active: apiFilters?.is_active,
    // values coming from baseFilter - it has priority from apiFilters
    ...baseFilter,
    // values coming from custom filters shared with baseFilter - has priority
    ...(customFilters?.tag ? { tags: customFilters?.tag } : {}),
    ...(customFilters?.assignedTag
      ? { assigned_tags: customFilters?.assignedTag }
      : {}),
    ...(customFilters?.exclude_tags
      ? { exclude_tags: customFilters?.exclude_tags }
      : {}),
    ...(customFilters?.preferred_clinic
      ? { preferred_clinic: customFilters?.preferred_clinic }
      : {}),

    // values coming only from custom filters
    payment_source_type: customFilters?.paymentSourceType,
    preferred_provider: customFilters?.preferredProvider,
    latest_referral_source: customFilters?.referralSource,
    latest_sub_referral_source: customFilters?.subReferralSource,
    crm_segment: customFilters?.crmSegment,
    manufacturer_warranty_is_expiring_in_less_than_days:
      customFilters?.manufacturerWarrantyIsExpiringInLessThanDays,
    last_outcome: customFilters?.last_outcome,
    preferred_communication_method:
      customFilters?.preferred_communication_method,
    sex: customFilters?.sex,
    insurer_any: customFilters?.insurer,
    managed_care_plan: customFilters?.managed_care_plan,
    primary_care_physician: customFilters?.primary_care_physician,
    referring_physician: customFilters?.referring_physician,
    region: customFilters?.region,
    manufacturer: customFilters?.manufacturer,
    has_hearing_aid: customFilters?.has_hearing_aid,
    latest_icd10_code: customFilters?.latest_icd10_code,
    zip_code: customFilters?.zip_code
  };
};

export const getPatientsEndpointWithQueryParameters = (
  filters: PatientFilters
) => {
  const { ordering, search } = filters;

  // Check if search contains multiple words (likely first + last name)
  const isFullNameSearch = search && search.trim().split(" ").length >= 2;

  // For full name searches, omit default ordering to let backend search relevance take precedence
  // Only apply ordering if explicitly provided by user OR if it's not a full name search
  const shouldApplyDefaultOrdering = !isFullNameSearch || ordering;

  const orderingString = shouldApplyDefaultOrdering
    ? [ordering, "last_name,first_name,id"].filter(Boolean).join(",")
    : ordering || "";

  const orderingParam = orderingString ? `&ordering=${orderingString}` : "";

  return `/patients/${formatValidQueryParamUrlCommaSeparated(
    formatPatientFilters({
      ...filters,
      search: search && encodeURIComponent(search)
    }),
    true
  )}${orderingParam}&limit=${
    filters.limit ?? DEFAULT_PATIENTS_LIMIT
  }&offset=${filters.offset ?? 0}&query=${filters.query ?? "{*}"}`;
};
